import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Popconfirm,
  Row,
  Col,
  Card,
  Typography,
  InputNumber,
  Upload,
  Radio,
  Image,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { newsAPI, uploadAPI } from '../api/client';
import { API_BASE_URL } from '../utils/constants';

const { TextArea } = Input;
const { Paragraph, Text } = Typography;

const NewsManager = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [coverFileList, setCoverFileList] = useState([]);
  /** 勿在 shouldUpdate 里切换挂载正文/外链字段，否则 validateFields 可能拿不到 content_*，PUT 体缺字段后端不会更新 */
  const detailKind = Form.useWatch('detail_kind', form) ?? 'text';
  const isLink = detailKind === 'link';

  const resolveAssetUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${API_BASE_URL}${path}`;
  };

  const loadArticles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await newsAPI.listArticles({ skip: 0, limit: 500 });
      setArticles(data);
    } catch (e) {
      message.error('加载新闻失败: ' + e.message);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);

  const openModal = (row = null) => {
    setEditing(row);
    if (row) {
      const { id: _id, created_at: _c, updated_at: _u, ...rest } = row;
      form.setFieldsValue({
        ...rest,
        detail_kind: row.detail_kind || 'text',
      });
      if (row.cover_image_url) {
        setCoverFileList([
          {
            uid: '-1',
            name: 'cover',
            status: 'done',
            url: resolveAssetUrl(row.cover_image_url),
          },
        ]);
      } else {
        setCoverFileList([]);
      }
    } else {
      form.resetFields();
      form.setFieldsValue({ detail_kind: 'text', sort_order: 0 });
      setCoverFileList([]);
    }
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditing(null);
    setCoverFileList([]);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      if (values.detail_kind === 'link') {
        values.content_zh = null;
        values.content_en = null;
      } else {
        values.redirect_url = null;
      }
      delete values.id;
      delete values.created_at;
      delete values.updated_at;
      if (editing) {
        await newsAPI.updateArticle(editing.id, values);
        message.success('已更新');
      } else {
        await newsAPI.createArticle(values);
        message.success('已创建');
      }
      closeModal();
      await loadArticles();
    } catch (err) {
      if (err.errorFields) return;
      message.error(err.message || '操作失败');
    }
  };

  const handleDelete = async (id) => {
    try {
      await newsAPI.deleteArticle(id);
      message.success('已删除');
      await loadArticles();
    } catch (e) {
      message.error('删除失败: ' + e.message);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 64 },
    { title: '名称', dataIndex: 'name', width: 100, ellipsis: true, render: (t) => t || '-' },
    {
      title: '中文标题',
      dataIndex: 'title_zh',
      width: 140,
      ellipsis: true,
      render: (t) => t || '-',
    },
    {
      title: '英文标题',
      dataIndex: 'title_en',
      width: 140,
      ellipsis: true,
      render: (t) => t || '-',
    },
    {
      title: '封面',
      dataIndex: 'cover_image_url',
      width: 72,
      render: (url) =>
        url ? (
          <Image src={resolveAssetUrl(url)} alt="" width={48} height={48} style={{ objectFit: 'cover' }} />
        ) : (
          '-'
        ),
    },
    {
      title: '详情',
      dataIndex: 'detail_kind',
      width: 72,
      render: (k) => (k === 'link' ? '外链' : '正文'),
    },
    { title: '排序', dataIndex: 'sort_order', width: 72 },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openModal(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id)}>
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Paragraph style={{ marginBottom: 8 }}>
          新闻内容存储在数据库独立表 <Text code>news_articles</Text>，与「模块 / 字段 / 列表项」无关。
        </Paragraph>
        <Text type="secondary">
          官网请使用 <Text code>GET /api/news/page</Text> 拉取页面信息与文章列表。
        </Text>
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal(null)}>
          添加新闻
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={articles}
        scroll={{ x: 900 }}
        pagination={{ pageSize: 15 }}
      />

      <Modal
        title={editing ? '编辑新闻' : '添加新闻'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={closeModal}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称（可选，内部备注）">
            <Input placeholder="可选" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title_zh" label="中文标题">
                <Input placeholder="中文标题" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title_en" label="英文标题">
                <Input placeholder="English title" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="sort_order" label="排序（数字越大越靠前）" initialValue={0}>
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item name="cover_image_url" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="封面图" extra="jpg/png/gif/webp，最大 5MB">
            <Upload
              listType="picture-card"
              maxCount={1}
              fileList={coverFileList}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const data = await uploadAPI.uploadCover(file);
                  form.setFieldsValue({ cover_image_url: data.url });
                  setCoverFileList([
                    {
                      uid: file.uid,
                      name: file.name,
                      status: 'done',
                      url: resolveAssetUrl(data.url),
                    },
                  ]);
                  onSuccess(data, file);
                  message.success('上传成功');
                } catch (err) {
                  message.error(err.message || '上传失败');
                  onError(err);
                }
              }}
              onRemove={() => {
                form.setFieldsValue({ cover_image_url: '' });
                setCoverFileList([]);
              }}
            >
              {coverFileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item name="detail_kind" label="详情类型" rules={[{ required: true, message: '请选择' }]}>
            <Radio.Group>
              <Radio value="text">正文</Radio>
              <Radio value="link">外链</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="redirect_url"
            label="详情链接"
            hidden={!isLink}
            dependencies={['detail_kind']}
            rules={[
              ({ getFieldValue: get }) => ({
                validator(_, value) {
                  if (get('detail_kind') !== 'link') return Promise.resolve();
                  if (!value || !String(value).trim()) {
                    return Promise.reject(new Error('请填写链接'));
                  }
                  try {
                    // eslint-disable-next-line no-new
                    new URL(value);
                  } catch {
                    return Promise.reject(new Error('无效 URL'));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Row gutter={16} style={{ display: isLink ? 'none' : undefined }}>
            <Col span={12}>
              <Form.Item name="content_zh" label="中文正文">
                <TextArea rows={6} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="content_en" label="英文正文">
                <TextArea rows={6} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default NewsManager;
